import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Lock, ShieldCheck, KeyRound, LogOut } from "lucide-react";
import { toast } from "sonner";

function PasswordField({ id, label, value, onChange, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <Label htmlFor={id} className="text-[12px] font-semibold text-[#595959] mb-1.5 block">{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 bg-white pr-10"
          data-testid={testid}
        />
        <button type="button" onClick={() => setShow((v) => !v)} aria-label="Mostrar/ocultar"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#8C8C8C] hover:text-[#1A1A1A]">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

export default function Cuenta() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOk("");
    if (!current || !next || !repeat) { setErr("Completá todos los campos."); return; }
    if (next.length < 8) { setErr("La nueva contraseña debe tener al menos 8 caracteres."); return; }
    if (next !== repeat) { setErr("Las contraseñas no coinciden."); return; }
    if (next === current) { setErr("La nueva contraseña debe ser distinta a la actual."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setOk("Contraseña actualizada. Vamos a cerrar sesión para que ingreses con la nueva.");
      toast.success("Contraseña actualizada");
      setTimeout(() => {
        logout();
        navigate("/login", { replace: true });
      }, 1800);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Error al cambiar la contraseña");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <PageHeader
        eyebrow="Seguridad"
        title="Mi cuenta"
        subtitle="Cambiá tu contraseña. Mantenela larga y única — al menos 8 caracteres."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-2 p-6 border-[#E5E0D8] rounded-2xl bg-white">
          <form onSubmit={submit} className="space-y-4" data-testid="cuenta-form">
            <PasswordField id="cur" label="Contraseña actual" value={current} onChange={setCurrent} testid="cuenta-actual" />
            <PasswordField id="next" label="Nueva contraseña" value={next} onChange={setNext} testid="cuenta-nueva" />
            <PasswordField id="repeat" label="Repetir nueva contraseña" value={repeat} onChange={setRepeat} testid="cuenta-repeat" />

            {err && <div className="text-sm text-[#8E2E2E] bg-[#FBEBEB] border border-[#F0CECE] rounded-lg p-3" data-testid="cuenta-error">{err}</div>}
            {ok && <div className="text-sm text-[#3F6B3C] bg-[#DAE6D7] border border-[#BCD3B7] rounded-lg p-3" data-testid="cuenta-ok">{ok}</div>}

            <Button type="submit" disabled={saving}
                    className="bg-[#C85A32] hover:bg-[#A64A29] text-white h-11 gap-2 px-6"
                    data-testid="cuenta-save">
              <KeyRound className="h-4 w-4" />
              {saving ? "Guardando..." : "Cambiar contraseña"}
            </Button>
          </form>
        </Card>

        <div className="space-y-4">
          <Card className="p-5 border-[#E5E0D8] rounded-2xl bg-white">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C] mb-2">Cuenta</div>
            <div className="font-display font-bold text-lg">{user?.nombre}</div>
            <div className="text-[12px] text-[#595959] font-mono break-all">{user?.email}</div>
            <div className="mt-3 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-[#F4EAE6] text-[#A64A29] uppercase tracking-wider font-semibold">
              {user?.role}
            </div>
          </Card>

          <Card className="p-5 border-[#E5E0D8] rounded-2xl bg-white">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C] mb-2">
              <ShieldCheck className="h-3.5 w-3.5" /> Tips de seguridad
            </div>
            <ul className="text-[13px] text-[#595959] space-y-1.5 list-disc pl-4">
              <li>Mínimo 8 caracteres.</li>
              <li>Usá una mezcla de letras y números.</li>
              <li>Nunca compartas tu contraseña por WhatsApp ni mail.</li>
              <li>Cambiala periódicamente.</li>
            </ul>
          </Card>

          <button type="button" onClick={() => { logout(); navigate("/login"); }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-[13px] text-[#8E2E2E] bg-white border border-[#E5E0D8] hover:bg-[#FBEBEB] rounded-lg"
                  data-testid="cuenta-logout">
            <LogOut className="h-4 w-4" /> Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
}
